import {Injectable} from '@angular/core';

import {ConstantsService} from '../../../../services/constants.service';
import {Company, User, UserRole} from '../../../../types/admin-data.types';
import {ApiClientService} from '../../../../services/api-client.service';
import {UrlService} from '../../../../services/url.service';
import {EmptyRowService} from '../../../../services/empty-row.service';
import {Observable} from 'rxjs/Observable';
import {ReplaySubject} from 'rxjs/ReplaySubject';
import {Building} from '../../../../types/models';
import {AccountPermissionsService} from '../../../../services/account-permissions.service';
import {DateTransformer} from '../../../../services/date-transformer.service';
import {filter, flatMap, tap} from 'rxjs/operators';
import {environment} from '../../../../../environments/environment';
import {createInitialNetworkEntity, resolveNetworkEntity} from '../../../../services/helper-functions';

declare const toastr: any;
declare const swal: any;
declare const $: any;

@Injectable()
export class UserService {
    spinnerShow = false;

    data: Array<User> = [];
    total: number | null = null;
    buildings: Array<any> = [];
    buildingsForRead: Array<any> = [];
    buildingsForUpdate: Array<any> = [];
    userRoles: Array<UserRole> = [];
    companies: Array<Company> = [];
    showUserPasswordResetModal: boolean = false;
    isShowDeletePopup: boolean = false;
    userInResetPasswordModal: any;

    public userTypes: Array<any> = [
        {id: 'super_admin', name: 'Admin'},
        {id: 'company_admin', name: 'Company Admin'},
        {id: 'property_manager', name: 'Staff'},
        {id: 'staff', name: 'Building Staff'},
        {id: 'resident', name: 'Resident'},
        {id: 'vendor', name: 'Vendor'},
    ];

    public multiselectBuildings: Array<any> = [];

    corporationNumberList = [];
    corporationNumberListForSearchSelect = [];

    constructor(public api: ApiClientService,
                public urlService: UrlService,
                public emptyRowService: EmptyRowService,
                public account: AccountPermissionsService) {
        this.account.onPermissionsUpdate.subscribe(() => {
            this.account.navigateIfServiceDisallowed('auth', '/home');
        });
    }

    get() {
        this.spinnerShow = true;
        const queryParams = this.urlService.getQueryParamsString();
        const url = `/api/users?expand=company,phones,lastActivity${queryParams}`;
        this.api.getJson('oauthUrl', url).subscribe(body => {
            this.getBuildings().subscribe((buildings) => {
                if (this.data && buildings && buildings.length) {
                    this.data = body.entities.map((item) => {
                        item = new User(item);

                        item.buildingsManager = item.buildingsManager.map((item) => {
                            return {
                                ...item,
                                building: buildings.find((building) => {
                                    return building.id == item.buildingId;
                                })
                            };
                        });

                        return item;
                    });
                    this.getFilteredBuildings();
                }
                this.emptyRowService.fillEmptyRow();
                this.spinnerShow = false;
            });

            this.total = body.total;
            if ($('.table')[0])
                $('.table')[0].scrollIntoView();
        }, error => this.api.errorHandler(error, 'Failed get Users'));
    }

    getByUrlQueryParams() {
        const queryParams = this.urlService.getQueryParamsString();
        const url = `/api/users?expand=company,phones,lastActivity${queryParams}`;
        return this.api.getJson('oauthUrl', url)
            .pipe(
                createInitialNetworkEntity(),
                resolveNetworkEntity(),
                tap(({finished}) => this.spinnerShow = !finished),
                tap(response => {
                    this.data = response.entities;
                    this.total = response.total;
                }),
                filter(body => body.entities.length > 0),
                flatMap(body => {
                    return this.getBuildings()
                        .pipe(
                            tap(
                                (buildings) => {
                                    if (this.data && buildings && buildings.length) {
                                        this.data = body.entities.map((item) => {
                                            item = new User(item);
                                            item.buildingsManager = item.buildingsManager.map((item) => {
                                                return {
                                                    ...item,
                                                    building: buildings.find((building) => {
                                                        return building.id == item.buildingId;
                                                    })
                                                };
                                            });
                                            return item;
                                        });
                                        this.getFilteredBuildings();
                                    }
                                }
                            ),
                        );
                }),
                tap(
                    () => {
                        if ($('.table')[0])
                            $('.table')[0].scrollIntoView();
                    },
                    (error) => {
                        this.api.errorHandler(error, 'Failed get Users');
                        this.emptyRowService.fillEmptyRow();
                        this.spinnerShow = false;
                    }
                )
            );
    }

    create(data: User): Observable<any> {
        return this.api.post('oauthUrl', '/api/v1/users.json', data).map((res) => {
            return res;
        }, (error) => {
            this.api.errorHandler(error, 'Create user failed');

            return error;
        });
    }

    update(id: number, data: any) {
        return this.api.patch('oauthUrl', `/api/v1/users/${id}.json`, data).map((res) => {
            this.account.init();
            return res;
        }, (error) => {
            this.api.errorHandler(error, 'Edit user failed');
        });
    }

    remove(id: number): Observable<any> {
        if (!id) return;

        const subject = new ReplaySubject<any>(null);

        swal(ConstantsService.getSwalConfig('Delete User'),
            () => {
                const url = `/api/users/${id}`;
                this.api.remove('oauthUrl', url).subscribe(
                    (res) => {
                        toastr.success('User has been deleted.');
                        if (this.data.length - 1 == 0 && this.urlService.queryParams.page != '1')
                            this.urlService.queryParams.page = (parseInt(this.urlService.queryParams.page) - 1).toString();
                        this.account.init();
                        this.get();
                        subject.next(res);
                    },
                    error => this.api.errorHandler(error, 'Delete User failed')
                );
            }
        );

        return subject;
    }

    public getBuildings(): Observable<Array<Building>> {
        const url = `/v1/buildings/short.json?per-page=10000&order-by=name|asc`;

        return this.api.get('domainBD', url).map(
            (res) => {
                this.multiselectBuildings = JSON.parse(res._body).entities.map((item) => {
                    return {
                        ...item,
                        itemName: item.name,
                        disabled: item.disabled
                    };
                });

                return this.buildings = JSON.parse(res._body).entities;
            },
            (error) => this.api.errorHandler(error, 'Failed get buildings')
        );
    }

    getFilteredBuildings() {
        this.buildingsForRead = this.account.getBuildingsForRead(this.buildings, 'auth');
        this.buildingsForUpdate = this.account.getBuildingsForUpdate(this.buildings, 'auth');
    }

    public getUserRoles() {
        const url = `/api/v1/role.json`;
        this.api.getInfiniteList('oauthUrl', url, 1000).subscribe(
            res => {
                this.userRoles = [];
                res.entities.map(i => this.userRoles.push(new UserRole(i)));
            },
            error => this.api.errorHandler(error, 'Failed get Roles')
        );
    }

    public getCompanies() {
        const url = `/api/v1/company.json?per-page=1000&order-by=name|asc`;
        this.api.get('oauthUrl', url).subscribe(
            res => {
                this.companies = [];
                const body = JSON.parse(res._body);
                body.entities.map(i => this.companies.push(new Company(i)));
            },
            error => this.api.errorHandler(error, 'Failed get Companies')
        );
    }

    transformDate(date: Date): string {
        return DateTransformer.transformDate(date);
    }

    canEdit(buildingId?: number) {
        return this.account.canUpdate('auth', buildingId);
    }

    canDelete(buildingId?: number) {
        return this.account.canDelete('auth', buildingId);
    }

    sendInvitation(itemData) {
        const url = `/api/users/send-invite/${itemData.id}`;
        this.api.get('oauthUrl', url).subscribe(
            () => {
                toastr.success('Invitation was resent successfully');
                this.get();
            },
            error => this.api.errorHandler(error, 'Failed to resent invitation')
        );
    }

    resendInvitation(itemData) {
        const url = `/api/users/send-invite/${itemData.id}`;
        this.api.get('oauthUrl', url).subscribe(
            () => toastr.success('Invitation was resent successfully'),
            error => this.api.errorHandler(error, 'Failed to resend invitation')
        );
    }


    getCorporationNumber() {
        this.corporationNumberList = [];
        const url = `/v1/corporation_number?per-page=10000&order-by=name|asc`;
        this.api.get('domainBD', url).subscribe(res => {
            const body = JSON.parse(res._body);
            this.corporationNumberList = body.entities;
            this.corporationNumberListForSearchSelect = this.corporationsToSearchSelect(this.corporationNumberList);
        });
    }

    loginAsUser(userEmail: string): Observable<any> {
        return this.api
            .post('oauthUrl', `/api/v1/token/su.json`, {client: environment.oauthClientId, user: userEmail})
            .pipe(tap((response) => {
                const expires_at = (response.expires_in - 600) * 1000;
                delete response.expires_in;
                const token = {...response, expires_at};
                localStorage.setItem('mcc_token', JSON.stringify(token));
                location.href = '/';
            }));
    }

    corporationsToSearchSelect(arr: Array<any>) {
        return arr.map(suite => ({value: suite.id, optionText: suite.name}));
    }

    showDeletePopup(data) {
        this.userInResetPasswordModal = data;
        this.isShowDeletePopup = true;
    }
}



// WEBPACK FOOTER //
// /app/src/app/views/admin/user/services/user.service.ts